import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

const TrainingContext = createContext(null);

export const TrainingProvider = ({ api, env, children }) => {
  const [version, setVersion] = useState(0);
  const invalidate = useCallback(() => setVersion((v) => v + 1), []);
  const value = useMemo(() => ({ api, env, version, invalidate }), [api, env, version, invalidate]);
  return <TrainingContext.Provider value={value}>{children}</TrainingContext.Provider>;
};

export const useTraining = () => {
  const ctx = useContext(TrainingContext);
  if (!ctx) throw new Error("useTraining must be used inside <TrainingProvider>");
  return ctx;
};

/** Navigation relative to the module's base path ("/training" or "/training-preview"). */
export const useTrainingNav = () => {
  const { env } = useTraining();
  const navigate = useNavigate();
  const href = useCallback(
    (p = "") => `${env.basePath}${p.startsWith("/") || !p ? p : `/${p}`}`,
    [env.basePath]
  );
  const go = useCallback((p, opts) => navigate(href(p), opts), [navigate, href]);
  return { href, go };
};

/**
 * Tiny data hook: runs `fn(api)` on mount / when deps change / after any mutation
 * (`invalidate`). Keeps the previous data while reloading to avoid flicker.
 */
export const useTrainingQuery = (fn, deps = []) => {
  const { api, version } = useTraining();
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const seq = useRef(0);
  const run = useCallback(() => {
    const id = (seq.current += 1);
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.resolve(fn(api))
      .then((data) => id === seq.current && setState({ data, loading: false, error: null }))
      .catch(
        (error) => id === seq.current && setState((s) => ({ data: s.data, loading: false, error }))
      );
  }, [api, ...deps]);
  useEffect(() => {
    run();
  }, [run, version]);
  return { ...state, reload: run };
};

export const useDocumentTitle = (title) => {
  useEffect(() => {
    const prev = document.title;
    if (title) document.title = `${title} · Formation`;
    return () => {
      document.title = prev;
    };
  }, [title]);
};
